import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import SVG from '@app/components/SVG';
import { isSwiftCodeValid } from '@app/domain/accountDetailsValidators';
import { getAccountValidationFields, getAccountValidationForLocalFormatFields } from '@app/domain/accountVerification';
import { ACCOUNT_FILED_EXACT_LENGTH } from '@app/domain/uiConsts';
import chevronLeft from '@app/images/chevronLeft.svg';
import { convertToTitleCase } from '@app/utils/stringUtils';
import { zodResolver } from '@hookform/resolvers/zod';
import { SergeeSearchResult } from '@mortee/domain/morteeAccountVerification';
import * as React from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { z } from 'zod';
import { AccountVerificationForm, validateIbanCode } from './searchPayeeUtils';

interface MissingFieldsPageProps {
  formData: AccountVerificationForm;
  missingFields: string[];
  setCountryCode: (countryCode: string) => void;
  onSubmit: (data: AccountVerificationForm) => Promise<void>;
  verificationResult: SergeeSearchResult | null;
  onBack: () => void;
  onSkip: () => void;
}

export default function AccountVerificationInitialMissingFieldsPageV2({
  formData,
  missingFields,
  verificationResult,
  onSubmit,
  onBack,
  onSkip,
}: MissingFieldsPageProps): JSX.Element {
  const countryCode = verificationResult?.searchRequest?.account?.countryCode;

  const initialRenderFieldsRef = React.useRef<Array<{ key: string; displayName: string; inputType?: string }>>([]);

  const renderFields = React.useMemo(() => {
    const fields: Array<{ key: string; displayName: string; inputType?: string }> = [];
    let fieldsConfig: ReturnType<typeof getAccountValidationFields> | undefined;
    let localFormatFieldsConfig: ReturnType<typeof getAccountValidationForLocalFormatFields> | undefined;
    if (countryCode) {
      fieldsConfig = getAccountValidationFields(countryCode);
      localFormatFieldsConfig = getAccountValidationForLocalFormatFields(countryCode);
    }

    missingFields.forEach((fieldName) => {
      if (!fieldName) return;
      if (fieldName === 'domesticFields' && fieldsConfig) {
        Object.keys(fieldsConfig).forEach((field) => {
          if (formData[field as keyof AccountVerificationForm]) return;
          fields.push({
            key: field,
            displayName: convertToTitleCase(fieldsConfig?.[field]?.displayName),
            inputType: fieldsConfig?.[field]?.inputType,
          });
        });
      } else if (fieldName === 'localFormat' && localFormatFieldsConfig?.localFormat) {
        // Handle CLABE/localFormat field
        fields.push({
          key: 'localFormat',
          displayName: localFormatFieldsConfig.localFormat.displayName || 'CLABE',
          inputType: localFormatFieldsConfig.localFormat.inputType,
        });
      } else if (fieldName !== 'domesticFields') {
        let displayName: string;
        if (fieldName === 'iban') {
          displayName = 'IBAN';
        } else if (fieldName === 'swiftBic') {
          displayName = 'SWIFT/BIC';
        } else {
          displayName = convertToTitleCase(fieldName);
        }
        fields.push({
          key: fieldName,
          displayName,
        });
      }
    });

    if (initialRenderFieldsRef.current.length === 0) {
      initialRenderFieldsRef.current = fields;
    }

    return initialRenderFieldsRef.current;
  }, [countryCode, missingFields, formData]);

  const requiredFieldsSchema = React.useMemo(() => {
    const shape: Record<string, any> = {};
    renderFields.forEach((field) => {
      if (field.key === 'iban') {
        shape[field.key] = z
          .string()
          .nonempty(`${field.displayName} is required`)
          .refine(async (value) => await validateIbanCode(value), {
            message: 'Invalid IBAN',
          });
      } else if (field.key === 'swiftBic') {
        shape[field.key] = z
          .string()
          .nonempty(`${field.displayName} is required`)
          .refine((value) => isSwiftCodeValid(value), {
            message: 'Invalid SWIFT/BIC code',
          });
      } else if (field.key === 'localFormat' && countryCode === 'MX') {
        // CLABE validation: exactly 18 digits, numeric
        shape[field.key] = z
          .string()
          .nonempty(`${field.displayName} is required`)
          .length(ACCOUNT_FILED_EXACT_LENGTH.MX.clabe, `Must be exactly ${ACCOUNT_FILED_EXACT_LENGTH.MX.clabe} digits`)
          .regex(/^\d+$/, 'Must be numeric');
      } else {
        shape[field.key] = z.string().nonempty(`${field.displayName} is required`);
      }
    });
    return z.object(shape);
  }, [renderFields, countryCode]);

  const form = useForm<AccountVerificationForm>({
    resolver: (zodResolver(requiredFieldsSchema) as unknown) as Resolver<AccountVerificationForm>,
    defaultValues: formData,
  });

  const handleFormSubmit = async (data: AccountVerificationForm): Promise<void> => {
    const mergedData = {
      ...formData,
      ...data,
    };
    await onSubmit(mergedData);
  };

  return (
    <div data-testid='missing-fields-page' id='missing-fields-page' className='min-h-screen bg-background'>
      <div className='w-full max-w-2xl mx-auto p-6 space-y-6 pt-24'>
        <div className='space-y-2'>
          <h1 className='text-2xl font-semibold'>Additional Information Required</h1>
          <p className='text-muted-foreground'>
            No results were found in the Knox repository. To proceed with the verification, please provide the following
            information to continue the search through our extended network.
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className='space-y-6'>
            {renderFields.map((field) => (
              <FormField
                data-testid={`missing-${field.key}`}
                key={field.key}
                control={form.control}
                name={field.key as keyof AccountVerificationForm}
                render={({ field: f }): JSX.Element => (
                  <FormItem>
                    <FormLabel>{field.displayName}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={`Enter ${field.displayName}`}
                        {...f}
                        className='!text-black'
                        type={field.inputType}
                        maxLength={
                          field.key === 'localFormat' && countryCode === 'MX' ? ACCOUNT_FILED_EXACT_LENGTH.MX.clabe : undefined
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}

            <div className='flex justify-between items-center mt-10'>
              <Button
                data-testid='missing-fields-back-button'
                variant='ghost'
                className='w-fit flex items-center gap-2'
                style={{ color: 'var(--foreground)' }}
                onClick={onBack}
                type='button'
              >
                <SVG accessibilityLabel={'Back'} image={chevronLeft} height={18} className='fill-foreground text-foreground' />{' '}
                Back
              </Button>
              <span className='w-fit flex items-center gap-2'>
                <Button
                  data-testid='missing-fields-skip-button'
                  variant='outline'
                  style={{ color: 'var(--foreground)', minWidth: 100 }}
                  onClick={onSkip}
                  type='button'
                >
                  Skip
                </Button>
                <Button
                  data-testid='missing-fields-search-button'
                  type='submit'
                  variant='default'
                  disabled={form.formState.isSubmitting}
                  style={{ minWidth: 100 }}
                >
                  {form.formState.isSubmitting ? (
                    <span className='animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block'></span>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </span>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
